import React from "react";
import { Link } from "react-router-dom";
import {
  FiDollarSign,
  FiSmartphone,
  FiTruck,
  FiPercent,
  FiMap,
  FiCheckCircle,
  FiArrowRight,
  FiArrowUpRight,
} from "react-icons/fi";
import IndiaCapabilityCard from "./IndiaCapabilityCard";
import MigrationStage from "./MigrationStage";

const capabilities = [
  {
    icon: FiDollarSign,
    title: "GST Configuration",
    description:
      "Configure GST registrations, posting groups, tax rates, HSN/SAC details and transaction workflows within Business Central.",
    accent: "blue",
  },
  {
    icon: FiSmartphone,
    title: "E-Invoicing Workflows",
    description:
      "Support structured invoice validation, IRN generation, acknowledgement details and QR-code workflows through appropriate integrations.",
    accent: "cyan",
  },
  {
    icon: FiTruck,
    title: "E-Way Bill Processes",
    description:
      "Connect dispatch and transaction information with e-way bill preparation for applicable movement of goods.",
    accent: "teal",
  },
  {
    icon: FiPercent,
    title: "TDS & TCS Setup",
    description:
      "Configure applicable deduction and collection workflows, posting structures and reporting requirements.",
    accent: "indigo",
  },
  {
    icon: FiMap,
    title: "Multi-Location Reporting",
    description:
      "Use dimensions, locations and structured reporting to analyse branches, divisions, departments and business units.",
    accent: "violet",
  },
  {
    icon: FiCheckCircle,
    title: "Financial Reconciliation",
    description:
      "Validate trial balance, receivables, payables, bank, inventory and tax information before moving toward go-live.",
    accent: "magenta",
  },
];

const migrationStages = [
  {
    number: "1",
    title: "Analyse",
    description: "Review Tally companies, masters, vouchers and reporting structures.",
  },
  {
    number: "2",
    title: "Map",
    description: "Map ledgers, items, customers, vendors and dimensions to Business Central.",
  },
  {
    number: "3",
    title: "Migrate",
    description: "Import approved data through controlled and repeatable migration processes.",
  },
  {
    number: "4",
    title: "Reconcile",
    description: "Compare key financial and operational reports before final acceptance.",
  },
];

const IndiaMap = () => (
  <svg
    viewBox="0 0 100 120"
    className="absolute -right-10 top-1/2 z-0 h-[34rem] w-[34rem] -translate-y-1/2 opacity-[0.03] lg:-right-20"
    aria-hidden="true"
  >
    <path
      d="M48 8
         C52 8, 58 9, 62 11
         C67 13, 70 16, 72 21
         C74 26, 75 30, 75 35
         C75 40, 77 44, 80 47
         C83 50, 85 54, 86 58
         C87 62, 88 67, 88 72
         C88 78, 86 84, 84 89
         C82 95, 79 100, 75 105
         C70 110, 64 113, 58 115
         C52 117, 46 117, 40 115
         C34 113, 28 110, 24 105
         C20 100, 17 95, 16 89
         C14 84, 13 78, 13 72
         C13 67, 14 62, 15 58
         C16 54, 18 50, 21 47
         C24 44, 26 40, 26 35
         C26 30, 27 26, 29 21
         C31 16, 34 13, 39 11
         C43 9, 44 8, 48 8Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1"
    />
  </svg>
);

const IndiaReady = () => {
  return (
    <section className="relative overflow-hidden bg-white py-20 md:py-24 lg:py-28">
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute -left-20 top-1/3 h-80 w-80 -translate-y-1/2 rounded-full bg-blue-500/[0.04] blur-[110px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute right-0 top-0 h-80 w-80 rounded-full bg-violet-500/[0.04] blur-[110px]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="about-fade-up mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-bold tracking-[0.12em] text-slate-600 uppercase shadow-sm">
            INDIA-READY ERP
          </span>

          <h2 className="mt-5 font-raleway font-extrabold leading-[1.12] tracking-tight text-slate-900 text-[clamp(1.75rem,4vw,2.75rem)]">
            Business Central configured for the realities of doing{" "}
            <span className="text-[#FFDB16] drop-shadow-[0_0_20px_rgba(255,219,22,0.4)]">
              business in India
            </span>
            .
          </h2>

          <p className="mt-5 font-openSans text-base leading-relaxed text-slate-600 md:text-lg">
            Bring finance, taxation, invoicing, inventory and multi-location
            operations together with a Business Central solution configured
            around Indian business requirements.
          </p>

          <p className="mt-5 font-openSans text-sm leading-relaxed text-slate-500 md:text-base">
            From GST setup and e-invoicing to Tally migration and financial
            reconciliation, Abscod helps businesses move to a modern ERP through
            a controlled and practical implementation process.
          </p>
        </div>

        {/* Capability grid */}
        <div className="relative mt-14">
          <IndiaMap />
          <div className="relative z-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map((card, index) => (
              <div
                key={card.title}
                style={{ animationDelay: `${(index + 1) * 0.05}s` }}
                className="about-fade-up h-full"
              >
                <IndiaCapabilityCard {...card} />
              </div>
            ))}
          </div>
        </div>

        {/* Migration panel */}
        <div className="about-fade-up about-delay-1 relative z-10 mt-14 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0a0f1f] via-[#10183a] to-[#0a0f1f] p-8 shadow-[0_0_80px_-30px_rgba(37,99,235,0.25)] md:p-12 lg:p-14">
          {/* Dot pattern */}
          <div
            className="pointer-events-none absolute inset-0 z-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }}
            aria-hidden="true"
          />

          <div className="relative z-10 grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-center">
            {/* Left: headline + CTAs */}
            <div className="flex flex-col">
              <span className="inline-block w-fit rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-bold tracking-[0.12em] text-white/80 uppercase backdrop-blur-sm">
                FROM TALLY TO BUSINESS CENTRAL
              </span>

              <h3 className="mt-5 font-raleway text-2xl font-extrabold leading-tight text-white md:text-3xl lg:text-4xl">
                Move your historical data without losing financial confidence.
              </h3>

              <p className="mt-4 max-w-xl font-openSans text-base leading-relaxed text-slate-300 md:text-lg">
                Abscod supports the structured migration of masters, opening
                balances, outstanding documents and selected transactions,
                followed by validation and parallel-run reconciliation.
              </p>

              {/* CTAs */}
              <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <Link
                  to="/contactus"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#FFDB16] px-7 py-3.5 text-sm font-semibold text-slate-950 shadow-[0_12px_40px_rgba(255,219,22,0.25)] transition duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_16px_50px_rgba(255,219,22,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f1f]"
                >
                  Plan Your India-Ready Implementation
                  <FiArrowRight
                    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={2.5}
                  />
                </Link>

                <Link
                  to="/contactus"
                  className="group inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition duration-300 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f1f]"
                >
                  Discuss Your Tally Migration
                  <FiArrowUpRight
                    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={2.5}
                  />
                </Link>
              </div>
            </div>

            {/* Right: migration stages */}
            <div className="grid gap-5 sm:grid-cols-2">
              {migrationStages.map((stage, index) => (
                <MigrationStage
                  key={stage.number}
                  number={stage.number}
                  title={stage.title}
                  description={stage.description}
                  delay={0.2 + index * 0.1}
                />
              ))}
            </div>
          </div>

          {/* Subtle glow */}
          <div
            className="pointer-events-none absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-blue-500/20 blur-[120px]"
            aria-hidden="true"
          />
        </div>
      </div>
    </section>
  );
};

export default IndiaReady;
